import { useEffect, useState } from 'react';

/**
 * Custom hook that animates a number from 0 to a target value.
 * 
 * @param {number|string} target - Target number (can include non-numeric chars like '+', '%', '/')
 * @param {boolean} shouldStart - Whether the animation should start
 * @param {number} duration - Animation duration in ms, default 2000
 * @returns {string} The current animated value as a string
 */
const useCountUp = (target, shouldStart, duration = 2000) => {
  const [current, setCurrent] = useState('0');

  useEffect(() => {
    if (!shouldStart) return;

    // Extract the numeric part and suffix
    const targetStr = String(target);
    const match = targetStr.match(/^([\d.]+)(.*)$/);
    if (!match) {
      setCurrent(targetStr);
      return;
    }

    const numericTarget = parseFloat(match[1]);
    const suffix = match[2];
    const isFloat = match[1].includes('.');
    const startTime = performance.now();
    let frameId;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentValue = numericTarget * eased;

      if (isFloat) {
        setCurrent(currentValue.toFixed(1) + suffix);
      } else {
        setCurrent(Math.floor(currentValue) + suffix);
      }

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [target, shouldStart, duration]);

  return current;
};

export default useCountUp;
